import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { generateAssessment } from 'services/vendor-assessment-service';
import { getVendors } from 'services/vendor-service';
import { getFrameworks } from 'services/frameworks-service';
import { Spinner } from 'Components/common';
import { Dialog, Autocomplete, Select, TextField } from 'Components/ui';
import {
  StepperRow,
  StepItem,
  StepColumn,
  StepCircle,
  StepCircleNum,
  StepLabel,
  StepConnector,
  StepContentWrapper,
  StepCard,
  StepTitle,
  StepSubtitle,
  LoadingWrapper,
  FieldGroup,
  FieldLabel,
  TierCard,
  TierCardTitle,
  TierCardDesc,
  MaturityHeader,
  MaturityLabel,
  MaturityDesc,
  MaturityDotsRow,
  MaturityDotCol,
  MaturityDot,
  MaturityDotLabel,
  FrameworkDropdownItem,
  FrameworkItemName,
  FrameworkItemMeta,
  ReviewGrid,
  ReviewCell,
  ReviewCellLabel,
  ReviewCellValue,
  ReviewCellValueColored,
  InfoAlert,
  InfoAlertText,
  NavWrapper,
  NavRight,
  CancelBtn,
  BackBtn,
  NextBtn,
} from './AssessmentWizard.styles';

const CheckSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const InfoCircleSVG = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

const MATURITY_DOTS = [0, 1, 2, 3, 4, 5];

// Some seed framework names contain embedded newlines. A single-line <input>
// silently strips those from its DOM value, so a label built straight from
// str(f.name) never matches back against what the input actually holds —
// MUI's Autocomplete compares the two on blur and clears the selection when
// they differ. Collapsing whitespace keeps the label stable across that trip.
const frameworkLabel = (f) =>
  str(f.name).replace(/\s+/g, ' ').trim() + (f.version ? ` (${f.version})` : '');

export const AssessmentWizard = ({ onSuccess, onCancel, tenant }) => {
  const { t } = useTranslation(['gap-analysis']);
  const queryClient = useQueryClient();

  const stepDefs = [
    { key: 'step1', label: t('gap-analysis:assessment.wizard.steps.step1') },
    { key: 'step2', label: t('gap-analysis:assessment.wizard.steps.step2') },
    { key: 'step3', label: t('gap-analysis:assessment.wizard.steps.step3') },
  ];

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    vendorId: '',
    frameworkId: '',
    targetMaturityLevel: 3,
    internalNotes: ''
  });
  const { data: vendorsData, isLoading: vendorsLoading } = useQuery(
    ['vendors', tenant],
    () => getVendors(tenant),
    { enabled: !!tenant }
  );

  const { data: frameworksData, isLoading: frameworksLoading } = useQuery(
    ['frameworks'],
    () => getFrameworks(),
    { staleTime: 5 * 60 * 1000 }
  );

  const vendors = vendorsData?.data?.data || [];
  const frameworks = frameworksData?.data || [];
  const selectedVendor = vendors.find(v => v._id === formData.vendorId);
  const selectedFramework = frameworks.find(f => f._id === formData.frameworkId);

  const generateMutation = useMutation(
    (data) => generateAssessment(data),
    {
      onSuccess: (response) => {
        if (response.data?.success === false) {
          Dialog.alert({
            icon: 'error',
            title: t('gap-analysis:assessment.wizard.swal.errorTitle'),
            message: response.data?.error || t('gap-analysis:assessment.wizard.swal.errorText'),
            elevated: true,
          });
          return;
        }
        queryClient.invalidateQueries('assessments');
        Dialog.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.wizard.swal.createdTitle'),
          html: `<p>${t('gap-analysis:assessment.wizard.swal.createdHtml', { count: response.data?.data?.controlCount || 0 })}</p>`,
          timer: 3000,
          showConfirmButton: true,
        });
        if (onSuccess) onSuccess(response.data);
      },
      onError: (error) => {
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.wizard.swal.errorTitle'),
          message: error.response?.data?.error || error.response?.data?.message || t('gap-analysis:assessment.wizard.swal.errorText'),
          elevated: true,
        });
      }
    }
  );

  const handleNext = () => {
    if (activeStep === 0 && !formData.vendorId) {
      Dialog.alert({
        icon: 'warning',
        title: t('gap-analysis:assessment.wizard.swal.noVendorTitle'),
        message: t('gap-analysis:assessment.wizard.swal.noVendorText'),
        elevated: true,
      });
      return;
    }
    if (activeStep === 1 && !formData.frameworkId) {
      Dialog.alert({
        icon: 'warning',
        title: t('gap-analysis:assessment.wizard.swal.noFrameworkTitle'),
        message: t('gap-analysis:assessment.wizard.swal.noFrameworkText'),
        elevated: true,
      });
      return;
    }
    if (activeStep === stepDefs.length - 1) {
      generateMutation.mutate({
        vendorId: formData.vendorId,
        frameworkId: formData.frameworkId,
        targetMaturityLevel: formData.targetMaturityLevel,
        internalNotes: formData.internalNotes
      });
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });
  const handleSelectChange = (field) => (value) => setFormData({ ...formData, [field]: value });

  const maturityKey = `l${formData.targetMaturityLevel}`;
  const maturityLevelLabel = t(`gap-analysis:assessment.wizard.step2.maturityLevel.${maturityKey}`);
  const maturityLevelDesc = t(`gap-analysis:assessment.wizard.step2.maturityDesc.${maturityKey}`);

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepCard>
            <StepTitle>{t('gap-analysis:assessment.wizard.step1.title')}</StepTitle>
            <StepSubtitle>{t('gap-analysis:assessment.wizard.step1.subtitle')}</StepSubtitle>

            {vendorsLoading ? (
              <LoadingWrapper><Spinner /></LoadingWrapper>
            ) : (
              <FieldGroup>
                <FieldLabel>{t('gap-analysis:assessment.wizard.step1.vendorLabel')} *</FieldLabel>
                <Select
                  name="vendorId"
                  value={formData.vendorId}
                  onChange={handleSelectChange('vendorId')}
                  options={[
                    { value: '', label: t('gap-analysis:assessment.wizard.step1.selectVendor'), disabled: true },
                    ...vendors.map(v => ({
                      value: v._id,
                      label: t('gap-analysis:assessment.wizard.step1.vendorOption', { name: str(v.name), tier: v.tier }),
                    })),
                  ]}
                />
              </FieldGroup>
            )}

            {selectedVendor && (
              <TierCard $tier={selectedVendor.tier}>
                <TierCardTitle $tier={selectedVendor.tier}>
                  {t(`gap-analysis:assessment.wizard.step1.tier${selectedVendor.tier}Title`)}
                </TierCardTitle>
                <TierCardDesc>
                  {t(`gap-analysis:assessment.wizard.step1.tier${selectedVendor.tier}Alert`)}
                </TierCardDesc>
              </TierCard>
            )}
          </StepCard>
        );

      case 1:
        return (
          <StepCard>
            <StepTitle>{t('gap-analysis:assessment.wizard.step2.title')}</StepTitle>
            <StepSubtitle>{t('gap-analysis:assessment.wizard.step2.subtitle')}</StepSubtitle>

            <FieldGroup>
              <FieldLabel>{t('gap-analysis:assessment.wizard.step2.frameworkLabel')} *</FieldLabel>
              <Autocomplete
                options={frameworks}
                getOptionLabel={frameworkLabel}
                value={selectedFramework ?? null}
                onChange={(f) => setFormData(d => ({ ...d, frameworkId: f?._id || '' }))}
                placeholder={t('gap-analysis:assessment.wizard.step2.searchFramework')}
                loading={frameworksLoading}
                disabled={frameworksLoading}
                renderOption={(props, f) => (
                  <FrameworkDropdownItem as="li" {...props} key={f._id}>
                    <FrameworkItemName>
                      {frameworkLabel(f)}
                    </FrameworkItemName>
                    <FrameworkItemMeta>
                      {str(f.source)}{f.geography ? ` · ${t(`gap-analysis:${f.geography}`)}` : ''}
                    </FrameworkItemMeta>
                  </FrameworkDropdownItem>
                )}
              />
            </FieldGroup>

            <MaturityHeader>
              <MaturityLabel>
                {t('gap-analysis:assessment.wizard.step2.targetMaturity')}{' '}
                <strong>{maturityLevelLabel}</strong>
              </MaturityLabel>
              <MaturityDesc>{maturityLevelDesc}</MaturityDesc>
              <MaturityDotsRow>
                {MATURITY_DOTS.map(val => (
                  <MaturityDotCol key={val}>
                    <MaturityDot
                      $active={formData.targetMaturityLevel === val}
                      onClick={() => setFormData({ ...formData, targetMaturityLevel: val })}
                    />
                    <MaturityDotLabel>L{val}</MaturityDotLabel>
                  </MaturityDotCol>
                ))}
              </MaturityDotsRow>
            </MaturityHeader>

            <FieldGroup style={{ marginBottom: 0 }}>
              <FieldLabel>{t('gap-analysis:assessment.wizard.step2.internalNotes')}</FieldLabel>
              <TextField
                multiline
                fullWidth
                rows={3}
                value={formData.internalNotes}
                onChange={handleChange('internalNotes')}
                placeholder={t('gap-analysis:assessment.wizard.step2.notesPlaceholder')}
              />
            </FieldGroup>
          </StepCard>
        );

      case 2:
        return (
          <>
            <StepCard>
              <StepTitle>{t('gap-analysis:assessment.wizard.step3.title')}</StepTitle>
              <StepSubtitle>{t('gap-analysis:assessment.wizard.step3.subtitle')}</StepSubtitle>
              <ReviewGrid>
                <ReviewCell>
                  <ReviewCellLabel>{t('gap-analysis:assessment.wizard.step3.vendorLabel')}</ReviewCellLabel>
                  <ReviewCellValue>{str(selectedVendor?.name)}</ReviewCellValue>
                </ReviewCell>
                <ReviewCell>
                  <ReviewCellLabel>{t('gap-analysis:assessment.wizard.step3.frameworkLabel')}</ReviewCellLabel>
                  <ReviewCellValue>{str(selectedFramework?.name)}</ReviewCellValue>
                </ReviewCell>
                <ReviewCell $last>
                  <ReviewCellLabel>{t('gap-analysis:assessment.wizard.step3.targetMaturity')}</ReviewCellLabel>
                  <ReviewCellValue>
                    {t(`gap-analysis:assessment.wizard.step2.maturityLevel.l${formData.targetMaturityLevel}`)}
                  </ReviewCellValue>
                </ReviewCell>
                <ReviewCell $last>
                  <ReviewCellLabel>{t('gap-analysis:assessment.wizard.step3.vendorTierLabel')}</ReviewCellLabel>
                  {selectedVendor && (
                    <ReviewCellValueColored $tier={selectedVendor.tier}>
                      {t(`gap-analysis:assessment.wizard.step1.tier${selectedVendor.tier}Title`)}
                    </ReviewCellValueColored>
                  )}
                </ReviewCell>
              </ReviewGrid>
            </StepCard>

            {selectedVendor?.tier === 3 ? (
              <InfoAlert>
                <InfoCircleSVG />
                <InfoAlertText>{t('gap-analysis:assessment.wizard.step3.tier3Info')}</InfoAlertText>
              </InfoAlert>
            ) : (
              <InfoAlert>
                <InfoCircleSVG />
                <InfoAlertText
                  dangerouslySetInnerHTML={{
                    __html: t('gap-analysis:assessment.wizard.step3.emailInfo', {
                      email: selectedVendor?.contactEmail || ''
                    })
                  }}
                />
              </InfoAlert>
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <StepperRow>
        {stepDefs.map((step, idx) => {
          const isDone = activeStep > idx;
          const isActive = activeStep === idx;
          const isLast = idx === stepDefs.length - 1;
          return (
            <StepItem key={step.key}>
              <StepColumn>
                <StepCircle $done={isDone} $active={isActive}>
                  {isDone ? <CheckSVG /> : <StepCircleNum $active={isActive}>{idx + 1}</StepCircleNum>}
                </StepCircle>
                <StepLabel $active={isActive}>{step.label}</StepLabel>
              </StepColumn>
              {!isLast && <StepConnector />}
            </StepItem>
          );
        })}
      </StepperRow>

      <StepContentWrapper>
        {getStepContent(activeStep)}

        <NavWrapper>
          <CancelBtn onClick={onCancel} disabled={generateMutation.isLoading}>
            {t('gap-analysis:assessment.wizard.cancelButton')}
          </CancelBtn>
          <NavRight>
            {activeStep > 0 && (
              <BackBtn
                disabled={generateMutation.isLoading}
                onClick={() => setActiveStep(prev => prev - 1)}
              >
                {t('gap-analysis:assessment.wizard.backButton')}
              </BackBtn>
            )}
            <NextBtn onClick={handleNext} disabled={generateMutation.isLoading}>
              {generateMutation.isLoading
                ? <Spinner $size={18} />
                : activeStep === stepDefs.length - 1
                  ? t('gap-analysis:assessment.wizard.generateButton')
                  : t('gap-analysis:assessment.wizard.nextButton')
              }
            </NextBtn>
          </NavRight>
        </NavWrapper>
      </StepContentWrapper>
    </div>
  );
};
